/* global angular */
import { Instant } from "@js-joda/core";
import moment from "moment";
import _ from "lodash";

import {
  declareAiCapability,
  provideAiCapability,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";

// Accounts receivable describing the filters worth driving from a sentence. It has three separate
// ranges - service, invoice and follow-up - plus contract, office and whether the invoice is paid.
declareAiCapability({
  id: "accountsReceivable.filter",
  title: "Filter the accounts receivable list",
  effect: "prefill",
  description:
    "Sets the filters on Billing > AR and re-runs its search: the service date range, the invoice date range, the follow-up date range, contract, office, whether the invoice is paid, and a free-text search over the table. Only works while that page is open.",
  location: "the filter bar of the Billing > AR page",
  scope: "page",
  permissionKey: "view_billing_section_ar",
  params: [
    {
      name: "serviceFrom",
      type: "date",
      required: false,
      description: "Earliest service date, as yyyy-MM-dd.",
    },
    {
      name: "serviceTo",
      type: "date",
      required: false,
      description: "Latest service date, as yyyy-MM-dd.",
    },
    {
      name: "invoiceFrom",
      type: "date",
      required: false,
      description: "Earliest invoice date, as yyyy-MM-dd.",
    },
    {
      name: "invoiceTo",
      type: "date",
      required: false,
      description: "Latest invoice date, as yyyy-MM-dd.",
    },
    {
      name: "followUpFrom",
      type: "date",
      required: false,
      description: "Earliest follow-up date, as yyyy-MM-dd.",
    },
    {
      name: "followUpTo",
      type: "date",
      required: false,
      description: "Latest follow-up date, as yyyy-MM-dd.",
    },
    {
      name: "contract",
      type: "enum",
      required: false,
      multiple: true,
      options: () => getArContractOptions(),
      description: "Limit to these contracts.",
    },
    {
      name: "office",
      type: "enum",
      required: false,
      multiple: true,
      options: () => getArOfficeOptions(),
      description: "Limit to these offices.",
    },
    {
      name: "search",
      type: "text",
      required: false,
      searchesFreeText: true,
      description: "Free text the table searches over every column.",
    },
  ],
  examples: [
    "accounts receivable for services last month",
    "AR for the france contract",
    "AR invoiced this year",
  ],
});

let arContracts = () => [];
let arOffices = () => [];

function getArContractOptions() {
  return arContracts().map((contract) => ({ value: String(contract.id), label: contract.name }));
}

function getArOfficeOptions() {
  return arOffices().map((office) => ({ value: String(office.id), label: office.name }));
}

//! @ngInject
export function billingAccountsReceivableCtrl(
  $scope,
  $rootScope,
  $filter,
  $timeout,
  DatabaseApi,
  toaster,
  NgTableParams,
  ngTableEventsChannel,
  itemSearchPageManager,
  ChecksCodesService,
  selectionLogic,
  generalUtils,
  $uibModal,
  FilterUtils,
  Storage,
  $state,
  $q,
  patientLookupService
) {
  const universalBOM = "\uFEFF";
  $rootScope.$on("got_agency_members", () => {
    $scope.loadAgencyMembers();
  });

  $scope.describeRarc = ChecksCodesService.describeRarc;
  $scope.describeCarc = ChecksCodesService.describeCarc;
  $scope.describeCagc = ChecksCodesService.describeCagc;

  $scope.toggleableColumns = [];
  $scope.markBiggerThan = 500000;
  $scope.arType = "Detailed";
  $scope.arTypeIndex = 0;
  $scope.availableSummaryDaysGroups = [];
  $scope.tableParams = null;
  $scope.globalFilter = { val: "" };
  $scope.offices = [];
  // Cost share is FreedomCare-only, so the filter that takes cost-share denials
  // off the balance is too.
  $scope.canIncludeCostShare = () => $rootScope.isFreedomCareAgency === true;

  $scope.filterBy = {
    checkLine: "",
    contractOverdue: "",
    alertOverdue: "",
    notes: "",
    serviceDateFrom: "",
    serviceDateTo: "",
    invoiceDateFrom: "",
    invoiceDateTo: "",
    serviceFollowUpDateFrom: "",
    serviceFollowUpDateTo: "",
    assignedMembers: [],
    exportBatch: "",
    paid: "",
    reasons: [],
    collectionStatuses: [],
    claimStatuses: [],
    groupByServiceDate: "",
    ageing: "",
    ageingByDepositDate: "",
    includeCostShare: "",
    services: [],
  };
  // One policy for the table, column picker and CSV. Stored column choices are
  // kept intact when a service or the cost-share checkbox is switched off.
  const costShareInvoiceColumns = new Set(["Cost Share Period", "Cost Share Frequency"]);
  const costShareVisitColumns = new Set(["Cost Share Attributed", "Cost Share Denied"]);
  const visitOnlyColumns = new Set([
    "Visit ID",
    "Schedule time",
    "Recorded time",
    "Caregiver",
    "Contract",
    "Service Code",
    "Service Code ID",
    "Units",
    "County",
    "Location",
    "No Payment Reason",
    "Collection Status",
    "Claim Status",
    "Note",
    "Follow Up Date",
    "Follow Up For",
    "Note Created At",
    "Note Created By",
    "Check Numbers",
    "Claim Numbers",
    "Adjustment Code",
    "Last Export Date",
    "Export Dates",
    "Notes",
    "Action",
  ]);
  const columnKey = (column) => (column === "Schedule Time" ? "Schedule time" : column);
  $scope.isColumnApplicable = (column) => {
    const key = columnKey(column);
    if (costShareInvoiceColumns.has(key)) {
      return (
        $scope.canIncludeCostShare() &&
        $rootScope.isPermittedByKey("view_patient_cost_share") &&
        $scope.isCostShareSelected()
      );
    }
    if (costShareVisitColumns.has(key)) {
      return (
        $scope.canIncludeCostShare() &&
        $scope.filterBy.includeCostShare === true &&
        !$scope.isOnlyCostShareSelected()
      );
    }
    return !visitOnlyColumns.has(key) || !$scope.isOnlyCostShareSelected();
  };
  $scope.isColumnVisible = (column) =>
    Boolean($scope.tableColumns?.[columnKey(column)]) && $scope.isColumnApplicable(column);

  $scope.invoiceIdsSet = new Set();

  $scope.ageing = {
    isOpen: false,
    options: {
      altInputFormats: ["MM/dd/yyyy"],
      startingDay: $rootScope.visitSettings.calendarStartDayOfTheWeek || 0,
    },
  };

  const initialize = () => {
    initPageManager();
    getOffices();
    $scope.pageManager.resetSearchParamsByUrl();

    if ($scope.agencyMemberNamesMap === undefined) {
      DatabaseApi.loadAgencyMembers();
    }
    $scope.loadAgencyMembers();

    // Load filters
    const storageFilters = FilterUtils.rootFilters.billingAccountsReceivableCtrl;
    if (storageFilters !== undefined) {
      $scope.filterBy = storageFilters;
    }

    $scope.loadItems();
    $scope.globalFilter.val = "";
  };

  $scope.$on("$destroy", function () {
    const filters = $scope.filterBy;
    FilterUtils.rootFilters.billingAccountsReceivableCtrl = angular.copy(filters);
  });

  function initTableColumns() {
    const columns = Storage.getObject("billingAccountsReceivableTableSettings");
    const defaultColumns = {
      Service: true,
      "Visit ID": true,
      Patient: true,
      "Patient Display ID": true,
      "Patient ID": true,
      "Admission ID": true,
      "Medicaid ID": true,
      // "Caregiver": true,
      "Visit Date": true,
      "Schedule time": true,
      // "Recorded time": true,
      Contract: true,
      "Service Code": true,
      "Service Code ID": true,
      Office: true,
      "Invoice Display ID": true,
      "Invoice ID": true,
      "Invoice Date": true,
      Units: true,
      Amount: true,
      Paid: true,
      Balance: true,
      "Payment Status": true,
      "No Payment Reason": true,
      "Collection Status": true,
      "Claim Status": true,
      Note: true,
      "Follow Up Date": true,
      "Follow Up For": true,
      "Note Created At": true,
      "Note Created By": true,
      "Check Numbers": true,
      "Claim Numbers": true,
      "Adjustment Code": true,
      "Last Export Date": true,
      "Export Dates": true,
      Notes: true,
      Action: true,
      Location: true,
      County: true,
    };

    if ($scope.canIncludeCostShare()) {
      defaultColumns["Cost Share Attributed"] = true;
      defaultColumns["Cost Share Denied"] = true;
      defaultColumns["Cost Share Period"] = true;
      defaultColumns["Cost Share Frequency"] = true;
    }

    const shownByDefaultColumns = new Set([
      "Invoice ID",
      "Invoice Date",
      "Units",
      "Amount",
      "Paid",
      "Balance",
      "Payment Status",
      "Collection Status",
      "Claim Status",
    ]);

    if (columns) {
      Object.keys(columns).forEach((t) => {
        if (defaultColumns[t] === undefined) {
          delete columns[t];
        }
      });

      Object.keys(defaultColumns).forEach((t) => {
        if (columns[t] === undefined) {
          columns[t] = defaultColumns[t];
        }
      });

      $scope.tableColumns = columns;
    } else {
      $scope.tableColumns = defaultColumns;
    }

    $scope.toggleableColumns = Object.keys($scope.tableColumns).filter(
      (col) => !shownByDefaultColumns.has(col)
    );

    Storage.setObject("billingAccountsReceivableTableSettings", $scope.tableColumns);
    $scope.$watch(
      "tableColumns",
      () => {
        if ($scope.tableColumns) {
          Storage.setObject("billingAccountsReceivableTableSettings", $scope.tableColumns);
          initTable($scope.invoicedServices);
        }
      },
      true
    );
  }

  const getOffices = () => {
    $scope.offices = DatabaseApi.offices();
    if (!$scope.offices || $scope.offices.length === 0) {
      DatabaseApi.get(
        `agencies/${$rootScope.agencyId}/agency_members/${$rootScope.agencyMemberId}/offices`
      ).then(
        (res) => {
          $scope.offices = res.data.offices;
          DatabaseApi.storeOffices(res.data.offices);
        },
        () => {
          toaster.pop("error", "Something went wrong", "could not get offices");
        }
      );
    }
  };

  const convertDateTimeFromString = (dateTimeValue) => new Date(dateTimeValue);
  const convertObjsArrToIdsStringSplitWithComma = (objsArr) =>
    objsArr.map((obj) => obj.id).join(",");
  const convertIdsStringToObjsArr = (strCommasSeparated) => strCommasSeparated.split(",");

  const initPageManager = () => {
    $scope.pageManager = itemSearchPageManager.createSearchPageManager("/ar_view");

    $scope.contractTypesDataManager = $scope.pageManager.getContractTypesDataManager();
    $scope.officeDataManager = $scope.pageManager.getOfficeDataManager();

    $scope.pageManager.initSearchParam("has-check", $scope.filterBy.checkLine);
    $scope.pageManager.initSearchParam("contract-overdue", $scope.filterBy.contractOverdue);
    $scope.pageManager.initSearchParam("alert-overdue", $scope.filterBy.alertOverdue);
    $scope.pageManager.initSearchParam("has-notes", $scope.filterBy.notes);
    $scope.pageManager.initSearchParam("group-by-service-date", $scope.filterBy.groupByServiceDate);
    $scope.pageManager.initSearchParam(
      "ageing-by-deposit-date",
      $scope.filterBy.ageingByDepositDate
    );
    $scope.pageManager.initSearchParam("include-cost-share", $scope.filterBy.includeCostShare);
    $scope.pageManager.initSearchParamDeprecated(
      "service-from",
      $scope.filterBy.serviceDateFrom,
      "service-from",
      generalUtils.formatDateByFilter,
      convertDateTimeFromString
    );
    $scope.pageManager.initSearchParamDeprecated(
      "service-to",
      $scope.filterBy.serviceDateTo,
      "service-to",
      generalUtils.formatDateByFilter,
      convertDateTimeFromString
    );
    $scope.pageManager.initSearchParamDeprecated(
      "invoice-from",
      $scope.filterBy.invoiceDateFrom,
      "invoice-from",
      generalUtils.formatDateByFilter,
      convertDateTimeFromString
    );
    $scope.pageManager.initSearchParamDeprecated(
      "invoice-to",
      $scope.filterBy.invoiceDateTo,
      "invoice-to",
      generalUtils.formatDateByFilter,
      convertDateTimeFromString
    );
    $scope.pageManager.initSearchParamDeprecated(
      "follow-up-from",
      $scope.filterBy.serviceFollowUpDateFrom,
      "follow-up-from",
      generalUtils.formatDateByFilter,
      convertDateTimeFromString
    );
    $scope.pageManager.initSearchParamDeprecated(
      "follow-up-to",
      $scope.filterBy.serviceFollowUpDateTo,
      "follow-up-to",
      generalUtils.formatDateByFilter,
      convertDateTimeFromString
    );
    $scope.pageManager.initContractTypesParam();
    $scope.pageManager.initOfficeParam();
    $scope.pageManager.initSearchParamDeprecated(
      "assigned-members",
      $scope.filterBy.assignedMembers,
      "assigned-members",
      convertObjsArrToIdsStringSplitWithComma,
      convertIdsStringToObjsArr
    );
    $scope.pageManager.initSearchParam("export-batch-display-id", $scope.filterBy.exportBatch, {
      placeholderValue: "",
      isEntity: true,
    });
    $scope.pageManager.initSearchParam("paid", $scope.filterBy.paid);
    $scope.pageManager.initSearchParamDeprecated(
      "reason",
      $scope.filterBy.reasons,
      "reason",
      convertObjsArrToIdsStringSplitWithComma,
      convertIdsStringToObjsArr
    );
    $scope.pageManager.initSearchParamDeprecated(
      "collection-status",
      $scope.filterBy.collectionstatuses,
      "collection-status",
      convertObjsArrToIdsStringSplitWithComma,
      convertIdsStringToObjsArr
    );
    $scope.pageManager.initSearchParamDeprecated(
      "claim-status",
      $scope.filterBy.claimStatuses,
      "claim-status",
      convertObjsArrToIdsStringSplitWithComma,
      convertIdsStringToObjsArr
    );
    $scope.pageManager.initSearchParamDeprecated(
      "ageing",
      $scope.filterBy.ageing,
      "ageing",
      generalUtils.formatDateByFilter,
      convertDateTimeFromString
    );
    $scope.pageManager.initSearchParamDeprecated(
      "services",
      $scope.filterBy.services,
      "services",
      (x) => x.join(","),
      convertIdsStringToObjsArr
    );
  };
  $scope.filteredList = [];
  const initSelection = function (items) {
    // $scope.filteredList = items;

    if (items) {
      items.forEach((item) => {
        item.mappedId = item.isCostShare
          ? `cs-invoice-${item.invoiceId}`
          : item.visitInstanceId
          ? `visit-${item.visitInstanceId}`
          : `pmpm-${item.pmpmBillingEntityId}`;
        item.service = item.isCostShare
          ? "Cost Share"
          : item.visitInstanceId
          ? "Direct Care"
          : item.pmpmBillingEntityId
          ? "Administrative Payment"
          : null;
      });
    }

    $scope.selectionLogic = selectionLogic.createNewLogic(
      (item) => {
        $scope.selectionLogic.addItemToCollection(item);
      },
      // Hope this doesn't come back to bite me..
      "mappedId"
    );

    if (items) {
      mapItems(items);
    }
  };

  const mapItems = (items) => {
    items.forEach(function (item) {
      const checkNumbers = [];
      if (item.checkNumbers) {
        item.checkNumbers.forEach((item) => {
          checkNumbers.push(item.checkNumber);
        });
      }
      item.admissionId = item.patientAdmissionId;
      item.medicaidId = item.patientMedicaidNumber;
      item.billedAmountDollars = $filter("centsToDollars")(item.billedAmount);
      item.paymentDollars = $filter("centsToDollars")(item.payment);
      item.costShareAttributedDollars = $filter("centsToDollars")(
        item.costShareAttributedAmount ?? 0
      );
      item.costShareDeniedDollars = $filter("centsToDollars")(item.costShareDeniedAmount ?? 0);
      item.balanceDollars = $filter("centsToDollars")(item.balance);
      if (item.latestNote) {
        item.latestNote.createByName = $scope.agencyMemberNamesMap[item.latestNote.createdBy];
        item.latestNote.followUpForName = $scope.agencyMemberNamesMap[item.latestNote.followUpFor];
      }
      item.adjustmentString =
        item.adjustment === null ? null : item.adjustment.groupCode + item.adjustment.reason;
      item.claimNumbersString = item.claimNumbers === null ? null : item.claimNumbers.join(", ");
      item.checkNumbersString = checkNumbers.join(", ");
      item.lastExportDate = item.exportDates.sort()[item.exportDates.length - 1] ?? null;
      $scope.selectionLogic.initItem(item);
    });
  };

  const initTable = (items) => {
    initSelection(items);
    $scope.arDetailedServices = items;

    const options = {
      count: 50,
    };
    $scope.tableParams = new NgTableParams(options, {
      dataset: items,
    });
    $scope.filteredList = [];
    ngTableEventsChannel.onAfterDataFiltered($scope.listeningFilterFunction, $scope);
  };
  $scope.listeningFilterFunction = (p, results, data) => {
    // updating filtered list with showed up list.
    $scope.filteredList = data;
  };

  const updateSearchParams = () => {
    $scope.pageManager.updateSearchParamValue("has-check", $scope.filterBy.checkLine);
    $scope.filterBy.checkLine = $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.checkLine;
    $scope.pageManager.updateSearchParamValue("contract-overdue", $scope.filterBy.contractOverdue);
    $scope.filterBy.contractOverdue =
      $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.contractOverdue;
    $scope.pageManager.updateSearchParamValue("alert-overdue", $scope.filterBy.alertOverdue);
    $scope.filterBy.alertOverdue =
      $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.alertOverdue;
    $scope.pageManager.updateSearchParamValue("has-notes", $scope.filterBy.notes);
    $scope.filterBy.notes = $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.notes;
    $scope.pageManager.updateSearchParamValue(
      "group-by-service-date",
      $scope.filterBy.groupByServiceDate
    );
    $scope.filterBy.groupByServiceDate =
      $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.groupByServiceDate;
    $scope.pageManager.updateSearchParamValue(
      "ageing-by-deposit-date",
      $scope.filterBy.ageingByDepositDate
    );
    // Why is everyone checking exportBatch?
    $scope.filterBy.ageingByDepositDate =
      $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.ageingByDepositDate;
    // Takes MA64 cost-share denials off the balance; billed and paid stay real.
    // Not a stored setting — a view choice, sent per search.
    $scope.pageManager.updateSearchParamValue(
      "include-cost-share",
      $scope.filterBy.includeCostShare
    );
    $scope.pageManager.updateSearchParamValue("service-from", $scope.filterBy.serviceDateFrom);
    $scope.filterBy.serviceDateFrom =
      $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.serviceDateFrom;
    $scope.pageManager.updateSearchParamValue("service-to", $scope.filterBy.serviceDateTo);
    $scope.filterBy.serviceDateTo =
      $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.serviceDateTo;
    $scope.pageManager.updateSearchParamValue("invoice-from", $scope.filterBy.invoiceDateFrom);
    $scope.filterBy.invoiceDateFrom =
      $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.invoiceDateFrom;
    $scope.pageManager.updateSearchParamValue("invoice-to", $scope.filterBy.invoiceDateTo);
    $scope.filterBy.invoiceDateTo =
      $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.invoiceDateTo;
    $scope.pageManager.updateSearchParamValue(
      "follow-up-from",
      $scope.filterBy.serviceFollowUpDateFrom
    );
    $scope.filterBy.serviceFollowUpDateFrom =
      $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.serviceFollowUpDateFrom;
    $scope.pageManager.updateSearchParamValue(
      "follow-up-to",
      $scope.filterBy.serviceFollowUpDateTo
    );
    $scope.filterBy.serviceFollowUpDateTo =
      $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.serviceFollowUpDateTo;
    $scope.pageManager.updateSearchParamValue("assigned-members", $scope.filterBy.assignedMembers);
    $scope.filterBy.assignedMembers =
      $scope.filterBy.exportBatch !== "" ? [] : $scope.filterBy.assignedMembers;
    $scope.pageManager.updateSearchParamValue("paid", $scope.filterBy.paid);
    $scope.filterBy.paid = $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.paid;
    $scope.pageManager.updateSearchParamValue("reason", $scope.filterBy.reasons);
    $scope.filterBy.reasons = $scope.filterBy.exportBatch !== "" ? [] : $scope.filterBy.reasons;
    $scope.pageManager.updateSearchParamValue(
      "collection-status",
      $scope.filterBy.collectionStatuses
    );
    $scope.filterBy.collectionStatuses =
      $scope.filterBy.exportBatch !== "" ? [] : $scope.filterBy.collectionStatuses;
    $scope.pageManager.updateSearchParamValue("claim-status", $scope.filterBy.claimStatuses);
    $scope.filterBy.claimStatuses =
      $scope.filterBy.exportBatch !== "" ? [] : $scope.filterBy.claimStatuses;
    $scope.pageManager.updateSearchParamValue("ageing", $scope.filterBy.ageing);
    $scope.filterBy.ageing = $scope.filterBy.exportBatch !== "" ? "" : $scope.filterBy.ageing;
    $scope.pageManager.updateSearchParamValue("services", $scope.filterBy.services);
    $scope.filterBy.services = $scope.filterBy.exportBatch !== "" ? [] : $scope.filterBy.services;

    // Why do all check exportBatch?

    $scope.pageManager.updateSearchParamValue(
      "export-batch-display-id",
      $scope.filterBy.exportBatch
    );
  };

  const costShareQuery = () => {
    const formatDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : undefined);
    const values = {
      asOfDate: formatDate($scope.filterBy.ageing) || moment().format("YYYY-MM-DD"),
      serviceDateFrom: formatDate($scope.filterBy.serviceDateFrom),
      serviceDateTo: formatDate($scope.filterBy.serviceDateTo),
      invoiceDateFrom: formatDate($scope.filterBy.invoiceDateFrom),
      invoiceDateTo: formatDate($scope.filterBy.invoiceDateTo),
    };
    return Object.entries(values)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join("&");
  };

  const loadCostShareRows = () => {
    if (
      !$scope.isCostShareSelected() ||
      !$scope.canIncludeCostShare() ||
      !$rootScope.isPermittedByKey("view_patient_cost_share")
    ) {
      return $q.resolve([]);
    }
    const url = `agencies/${$rootScope.agencyId}/agency_members/${$rootScope.agencyMemberId}`;
    return DatabaseApi.get(`${url}/patient_cost_share/ar_view?${costShareQuery()}`).then((res) =>
      patientLookupService.byIds(res.data.items.map((row) => row.patientId)).then((patients) =>
        res.data.items.map((row) => {
          const patient = patients.get(String(row.patientId));
          return {
            ...row,
            isCostShare: true,
            visitInstanceId: null,
            pmpmBillingEntityId: null,
            patientAdmissionId: patient?.admissionId ?? null,
            patientMedicaidNumber: patient?.medicaidNumber ?? null,
            visitDate: row.dueDate,
            invoiceCreatedAt: row.generatedAt,
            invoiceDisplayId: `Cost Share #${row.invoiceId}`,
            billedAmount: row.amountCents,
            payment: row.paidCents,
            balance: row.outstandingCents,
            billingUnitsThousandths: null,
            costShareAttributedAmount: null,
            costShareDeniedAmount: null,
            costSharePeriod: `${moment(row.invoicePeriodStart).format("MM/DD/YYYY")} – ${moment(
              row.invoicePeriodEnd
            ).format("MM/DD/YYYY")}`,
            costShareFrequency: { weekly: "Weekly", biweekly: "Biweekly", monthly: "Monthly" }[
              row.scheduleFrequency
            ],
            adjustment: null,
            checkNumbers: null,
            claimNumbers: null,
            exportDates: [],
            latestNote: null,
            notes: [],
          };
        })
      )
    );
  };

  // Keep both service types in one result set, including selection and CSV.
  let searchSequence = 0;
  $scope.searchAccountsReceivableVisits = () => {
    updateSearchParams();
    const sequence = ++searchSequence;
    const arType = $scope.arType;
    $scope.isArLoading = true;
    const visits = $scope.isOnlyCostShareSelected()
      ? $q.resolve({ data: { invoicedServices: [], contractSummaries: [] } })
      : $scope.pageManager.executeSearch();
    return $q
      .all([visits, loadCostShareRows()])
      .then(([res, costShare]) => {
        if (sequence !== searchSequence) return;
        if (arType === "Detailed") {
          $scope.invoicedServices = [...res.data.invoicedServices, ...costShare];
          initTableColumns();
          initTable($scope.invoicedServices);
          setInvoiceIdsSet();
          if ($scope.globalFilter.val) $scope.applyGlobalSearch($scope.globalFilter.val);
        } else {
          const summaries = [...res.data.contractSummaries];
          if (costShare.length) {
            const grouped = new Map();
            const asOfDate = moment($scope.filterBy.ageing || undefined).startOf("day");
            costShare.forEach((row) => {
              const age = asOfDate.diff(moment(row.dueDate), "days");
              const daysPastGroup = Math.floor(Math.max(0, age - 1) / 30) * 30;
              const group = grouped.get(daysPastGroup) || {
                daysPastGroup,
                visitCount: 0,
                billedAmount: 0,
                paidAmount: 0,
                balanceAmount: 0,
              };
              group.visitCount++;
              group.billedAmount += row.billedAmount;
              group.paidAmount += row.payment;
              group.balanceAmount += row.balance;
              grouped.set(daysPastGroup, group);
            });
            summaries.push({
              isCostShare: true,
              contractTypeId: null,
              summaries: [...grouped.values()],
            });
          }
          $scope.summaryTableData = mapSummaryData(summaries);
        }
      })
      .catch(() => {
        if (sequence === searchSequence) toaster.pop("error", "Failed to load accounts receivable");
      })
      .finally(() => {
        if (sequence === searchSequence) $scope.isArLoading = false;
      });
  };

  $scope.runArSearch = () => $scope.searchAccountsReceivableVisits();

  function setInvoiceIdsSet() {
    $scope.invoiceIdsSet = new Set();

    $scope.invoicedServices.forEach((iv) => {
      $scope.invoiceIdsSet.add(`${iv.isCostShare ? "cost-share" : "invoice"}-${iv.invoiceId}`);
    });
  }

  function mapSummaryData(items) {
    // Rebuild from scratch on every search so groups from a previous search
    // don't linger. getTotalItemSummaries (called below) repopulates this.
    $scope.availableSummaryDaysGroups = [];

    const resultItems = items.map((item) => {
      const contractType = item.isCostShare
        ? null
        : $scope.contractTypesDataManager.getContractTypeById(item.contractTypeId);

      const groupedSummaries = _.groupBy(item.summaries, "daysPastGroup");

      // Get grouped summaries - singled objects arrays out
      Object.keys(groupedSummaries).map((daysPastGroupKey) => {
        if (angular.isArray(groupedSummaries[daysPastGroupKey])) {
          groupedSummaries[daysPastGroupKey] = groupedSummaries[daysPastGroupKey][0];
        }
      });

      return {
        ...item,
        contractTypeName: item.isCostShare
          ? "Cost Share"
          : (contractType && contractType.name) || item.contractId,
        ...groupedSummaries,
        total: getTotalItemSummaries(item.summaries),
      };
    });
    sortNumbersArray($scope.availableSummaryDaysGroups);

    // The server only returns buckets that actually have data, so a 30-day
    // bucket with no invoices (e.g. 210-240) would be missing entirely. That
    // dropped the column and shifted every later bucket one position behind,
    // making the labels/clicks desync from the real data. Fill the range so
    // every 30-day bucket from 0 up to the largest present group is shown;
    // empty buckets render as "---".
    if ($scope.availableSummaryDaysGroups.length > 0) {
      const maxGroup =
        $scope.availableSummaryDaysGroups[$scope.availableSummaryDaysGroups.length - 1];
      const contiguousGroups = [];
      for (let days = 0; days <= maxGroup; days += 30) {
        contiguousGroups.push(days);
      }
      $scope.availableSummaryDaysGroups = contiguousGroups;
    }

    updateGroupsTotals(resultItems);
    return resultItems;
  }

  const getTotalItemSummaries = (summaries) => {
    return summaries.reduce(
      (total, summary) => {
        if ($scope.availableSummaryDaysGroups.indexOf(summary.daysPastGroup) === -1) {
          $scope.availableSummaryDaysGroups.push(summary.daysPastGroup);
        }
        total.visitCount += summary.visitCount;
        total.billedAmount += summary.billedAmount;
        total.paidAmount += summary.paidAmount;
        total.balanceAmount += summary.balanceAmount;
        return total;
      },
      { visitCount: 0, billedAmount: 0, paidAmount: 0, balanceAmount: 0 }
    );
  };

  const updateGroupsTotals = (items) => {
    $scope.summaryGroupTotals = $scope.availableSummaryDaysGroups.map((group) =>
      items.reduce(
        (groupSum, item) => {
          const groupFound = item.summaries.find((summary) => summary.daysPastGroup === group);
          if (groupFound !== undefined) {
            groupSum.visitCount += groupFound.visitCount;
            groupSum.billedAmount += groupFound.billedAmount;
            groupSum.paidAmount += groupFound.paidAmount;
            groupSum.balanceAmount += groupFound.balanceAmount;
          }
          return groupSum;
        },
        { visitCount: 0, billedAmount: 0, paidAmount: 0, balanceAmount: 0 }
      )
    );

    //   Sum a total of all rows totals
    $scope.summaryGroupTotals.push(
      items.reduce(
        (sumTotal, item) => {
          if (item.total !== undefined) {
            sumTotal.visitCount += item.total.visitCount;
            sumTotal.billedAmount += item.total.billedAmount;
            sumTotal.paidAmount += item.total.paidAmount;
            sumTotal.balanceAmount += item.total.balanceAmount;
          }
          return sumTotal;
        },
        { visitCount: 0, billedAmount: 0, paidAmount: 0, balanceAmount: 0 }
      )
    );
  };

  const sortNumbersArray = (arr) => {
    return arr.sort((a, b) => {
      if (a > b) {
        return 1;
      } else if (a < b) {
        return -1;
      } else {
        return 0;
      }
    });
  };

  $scope.handleSummaryGroupClick = (row, groupFromDaysAgo, groupToDaysAgo) => {
    if (!row[groupToDaysAgo]) {
      console.error(`Invalid group: ${groupToDaysAgo}`);
      return;
    }

    const asOfDate = $scope.filterBy.ageing ? moment($scope.filterBy.ageing) : moment();

    const groupFromFilter = new Date(moment(asOfDate).subtract(groupFromDaysAgo - 1, "days")); // -1 since the query includes end date
    const groupToFilter = new Date(moment(asOfDate).subtract(groupToDaysAgo, "days"));

    const fromKey =
      row.isCostShare || $scope.filterBy.groupByServiceDate ? "serviceDateFrom" : "invoiceDateFrom";
    const toKey =
      row.isCostShare || $scope.filterBy.groupByServiceDate ? "serviceDateTo" : "invoiceDateTo";

    $scope.filterBy[fromKey] = groupFromFilter;
    $scope.filterBy[toKey] = groupToFilter;

    $scope.pageManager.searchParams.selectedContractTypes.value = row.isCostShare
      ? []
      : [{ id: row.contractTypeId }];
    if (row.isCostShare) $scope.filterBy.services = ["COST_SHARE"];

    $scope.changeARType("Detailed");
    $scope.searchAccountsReceivableVisits();
  };

  $scope.$on("got_data", () => {
    $scope.loadItems();
  });

  $scope.$on("refresh_visits", function () {
    $scope.loadItems();
  });

  $scope.assignedMembersExtraSettings = {
    styleActive: true,
    scrollable: true,
    scrollableHeight: "400px",
    enableSearch: true,
  };

  $scope.arSettingsExtraSettings = {
    styleActive: true,
    scrollable: true,
    scrollableHeight: "400px",
    enableSearch: true,
    displayProp: "text",
  };

  $scope.loadAgencyMembers = () => {
    $scope.agencyMemberNamesMap = DatabaseApi.getAllAgencyMembersNames();
    $scope.assignedMembersOptions = DatabaseApi.getAgencyMembersArr().filter(
      (member) => member.status === "Active"
    );

    $scope.filteredAssignedMembersOptions = $scope.assignedMembersOptions
      .filter((agencyMember) => {
        return (
          agencyMember.officeIds.findIndex((agencyMemberOfficeId) =>
            $rootScope.user.agencyMember.officeIds.includes(agencyMemberOfficeId)
          ) !== -1
        );
      })
      .sort(generalUtils.sortByDisplayName);
  };

  arContracts = () => $scope.contractTypesDataManager?.getActiveContractTypes() ?? [];
  arOffices = () => $scope.officeDataManager?.getOffices() ?? [];

  const releaseAccountsReceivableFilter = provideAiCapability(
    "accountsReceivable.filter",
    (args) => {
      const applied = [];
      const asList = (value) => (Array.isArray(value) ? value : [value]);
      const asYmd = (text) => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(String(text))) {
          throw new Error("Could not read the date " + text + ".");
        }

        return String(text);
      };
      const pickAll = (available, requested, what) =>
        asList(requested).map((one) => {
          const found = (available ?? []).find((item) => String(item.id) === String(one));

          if (found === undefined) {
            throw new Error("This agency has no " + what + " " + one + ".");
          }

          return found;
        });

      for (const [arg, key, label] of [
        ["serviceFrom", "serviceDateFrom", "service from"],
        ["serviceTo", "serviceDateTo", "service to"],
        ["invoiceFrom", "invoiceDateFrom", "invoiced from"],
        ["invoiceTo", "invoiceDateTo", "invoiced to"],
        ["followUpFrom", "serviceFollowUpDateFrom", "follow-up from"],
        ["followUpTo", "serviceFollowUpDateTo", "follow-up to"],
      ]) {
        if (args[arg] !== undefined) {
          $scope.filterBy[key] = asYmd(args[arg]);
          applied.push(label + " " + String(args[arg]));
        }
      }

      if (args.contract !== undefined) {
        const contracts = pickAll(arContracts(), args.contract, "contract");

        $scope.pageManager.searchParams.selectedContractTypes.value = contracts;
        applied.push(contracts.map((contract) => contract.name).join(" and "));
      }

      if (args.office !== undefined) {
        const offices = pickAll(arOffices(), args.office, "office");

        $scope.pageManager.searchParams.selectedOffices.value = offices;
        applied.push(offices.map((office) => office.name).join(" and "));
      }

      if (args.search !== undefined) {
        $scope.globalFilter.val = String(args.search);
        applied.push('"' + String(args.search) + '"');
      }

      $scope.loadItems($scope.globalFilter.val);

      return applied.length === 0
        ? "Re-ran the accounts receivable search"
        : "Showing accounts receivable: " + applied.join(", ");
    }
  );

  $scope.$on("$destroy", releaseAccountsReceivableFilter);

  $scope.loadItems = (search) => {
    getArClaimStatuses();
    getArReasons();
    getArCollectionStatuses();
    $scope.loadAgencyMembers();

    if (search) {
      $scope.searchAccountsReceivableVisits();
    }
  };

  $scope.applyGlobalSearch = (term) => {
    const filter = { $: term };
    if ($scope.tableParams) {
      angular.extend($scope.tableParams.filter(), filter);
      const invoicedServices = $filter("filter")(
        $scope.arDetailedServices,
        $scope.globalFilter.val
      );
      initSelection(invoicedServices);
    }
  };

  // get arClaimStatuses
  const getArClaimStatuses = () => {
    DatabaseApi.get(
      `agencies/${$rootScope.agencyId}/agency_members/${$rootScope.agencyMemberId}/ar_claim_status_refs`
    ).then(
      (res) => {
        $scope.arClaimStatuses = res.data.claimStatusRefs;
        DatabaseApi.storeArClaimStatuses($scope.arClaimStatuses);
        $scope.arClaimStatuses = $scope.arClaimStatuses.filter((obj) => obj.active);
      },
      () => {
        toaster.pop("error", "Something went wrong", "could not get claim statuses");
      }
    );
  };

  // get arReasons
  const getArReasons = () => {
    DatabaseApi.get(
      `agencies/${$rootScope.agencyId}/agency_members/${$rootScope.agencyMemberId}/ar_reason_refs`
    ).then(
      (res) => {
        $scope.arReasons = res.data.arReasonRef;
        DatabaseApi.storeArReasons($scope.arReasons);
        $scope.arReasons = $scope.arReasons.filter((obj) => obj.active);
      },
      () => {
        toaster.pop("error", "Something went wrong", "could not get reasons");
      }
    );
  };

  // get arCollectionStatuses
  const getArCollectionStatuses = () => {
    DatabaseApi.get(
      `agencies/${$rootScope.agencyId}/agency_members/${$rootScope.agencyMemberId}/ar_collection_status_refs`
    ).then(
      (res) => {
        $scope.arCollectionStatuses = res.data.collectionStatusRefs;
        DatabaseApi.storeArCollectionStatuses($scope.arCollectionStatuses);
      },
      () => {
        toaster.pop("error", "Something went wrong", "could not get collection statuses");
      }
    );
  };

  $scope.toggleBtnGroupValue = (paramName, toggledButton) => {
    switch ($scope.filterBy[paramName]) {
      case "": {
        $scope.filterBy[paramName] = toggledButton;
        break;
      }
      case null: {
        $scope.filterBy[paramName] = !toggledButton;
        break;
      }
      default: {
        $scope.filterBy[paramName] = toggledButton === $scope.filterBy[paramName] ? "" : null;
        break;
      }
    }
  };

  $scope.toggleServices = (service) => {
    const services = $scope.filterBy.services;
    const index = services.indexOf(service);
    if (index === -1) {
      services.push(service);
    } else {
      services.splice(index, 1);
    }
  };

  $scope.isCostShareSelected = () =>
    $scope.filterBy.services && $scope.filterBy.services.indexOf("COST_SHARE") !== -1;

  $scope.isOnlyCostShareSelected = () =>
    $scope.filterBy.services.length === 1 && $scope.isCostShareSelected();

  $scope.clickTableRow = (row) => {
    if (row.isCostShare) $rootScope.openPatientModal(row.patientId);
    else if (row.visitInstanceId) $rootScope.openVisitInstanceModal(row.visitInstanceId);
  };

  $scope.openModalToAddNotes = (visitInstanceIds, pmpmBillingEntityIds) => {
    const modalInstance = $uibModal.open({
      templateUrl: "admin/views/invoiced-visits-add-notes-modal.html",
      size: "lg",
      controller: "invoicedVisitsAddNotesModalCtrl",
      resolve: {
        visitInstanceIds: () => visitInstanceIds,
        pmpmBillingEntityIds: () => pmpmBillingEntityIds,
        arReasons: () => $scope.arReasons,
        arClaimStatuses: () => $scope.arClaimStatuses,
        arCollectionStatuses: () => $scope.arCollectionStatuses,
        agencyMembers: () => $scope.assignedMembersOptions,
      },
    });

    modalInstance.result.then((res) => {
      if (res.success === "success") {
        $scope.attachNotesLocally(res.data);
        // $scope.searchAccountsReceivableVisits();
      }
    });
  };

  $scope.attachNotesLocally = (note) => {
    const psuedoNote = {
      arReasonText: $scope.arReasons?.find((reason) => reason.id === note.arReasonId)?.text || null,
      claimStatusText:
        $scope.arClaimStatuses?.find((status) => status.id === note.claimStatusRefId)?.text || null,
      collectionStatusText:
        $scope.arCollectionStatuses?.find((status) => status.id === note.collectionStatusRefId)
          ?.text || null,
      createdAt: Instant.now().toString(),
      createdBy: $rootScope.agencyMemberId,
      followUpDate: note.followUpDate,
      followUpFor: note.followupFor,
      noteText: note.text,
      psuedoNote: true,
    };
    const visitSet = new Set(note.visitInstanceIds);
    const pmpmSet = new Set(note.pmpmBillingEntityIds);

    // Don't change this to selected. Notes can be attached to unselected.
    const allServices = $scope.selectionLogic.data.items;

    for (const visit of allServices) {
      if (visitSet.has(visit.visitInstanceId) || pmpmSet.has(visit.pmpmBillingEntityId)) {
        visit.latestNote = psuedoNote;

        if (visit.notes === null) {
          visit.notes = [psuedoNote];
        } else {
          visit.notes.push(psuedoNote);
        }
      }
    }
  };

  $scope.hasSelectedVisitServices = () =>
    $scope.selectionLogic
      ?.getSelectedItems()
      .some((row) => row.visitInstanceId || row.pmpmBillingEntityId) || false;

  $scope.openModalToAddNotesToSelection = () => {
    const visitInstanceIds = [];
    const pmpmBillingEntityIds = [];
    $scope.selectionLogic.getSelectedItems().forEach((invoicedVisit) => {
      if (invoicedVisit.pmpmBillingEntityId) {
        pmpmBillingEntityIds.push(invoicedVisit.pmpmBillingEntityId);
      }
      if (invoicedVisit.visitInstanceId) {
        visitInstanceIds.push(invoicedVisit.visitInstanceId);
      }
    });

    if (visitInstanceIds.length || pmpmBillingEntityIds.length)
      $scope.openModalToAddNotes(visitInstanceIds, pmpmBillingEntityIds);
  };

  $scope.openModalToAddNoteByEntityId = (visitInstanceId, pmpmBillingEntityId) => {
    $scope.openModalToAddNotes(
      visitInstanceId ? [visitInstanceId] : [],
      pmpmBillingEntityId ? [pmpmBillingEntityId] : []
    );
  };

  $scope.openModalViewNotesByVisitId = (visitInstanceId, notes) => {
    $uibModal.open({
      templateUrl: "admin/views/invoiced-visit-notes-modal.html",
      size: "lg",
      controller: "invoicedVisitNotesModalCtrl",
      resolve: {
        visitInstanceId: () => visitInstanceId,
        agencyMembers: () => $scope.assignedMembersOptions,
        notes: () => notes,
      },
    });
  };

  $scope.changeARType = (type) => {
    $scope.arType = type;
    switch (type) {
      case "Detailed":
        $scope.arTypeIndex = 0;
        $scope.pageManager.updateRelativeURL("/ar_view");
        break;
      case "Summary":
        $scope.arTypeIndex = 1;
        $scope.pageManager.updateRelativeURL("/ar_view_summary");
        break;
    }
  };

  $scope.exportTable = (arType, selected) => {
    if (arType !== "Summary") {
      $scope.exportTableDetailed(selected);
    } else {
      $scope.exportTableSummary(selected);
    }
  };

  $scope.exportTableSummary = (selected) => {
    $scope.loadingCSV = true;

    const rows = [];

    // This is a duplicate of the html page logic.

    const titles = ["Contract"];
    for (const group of $scope.availableSummaryDaysGroups) {
      titles.push(`${group}-${group + 30} days`);
    }
    titles.push("Total");
    rows.push(titles);

    for (const dataRow of $scope.summaryTableData) {
      if (
        !selected ||
        $scope.availableSummaryDaysGroups.some(
          (group) => dataRow[group] && dataRow[group].balanceAmount > $scope.markBiggerThan * 100
        ) ||
        (dataRow && dataRow.total.balanceAmount > $scope.markBiggerThan * 100)
      ) {
        const row = [dataRow.contractTypeName];
        for (const group of $scope.availableSummaryDaysGroups) {
          row.push(
            '"' +
              $filter("centsToDollars")(dataRow[group] ? dataRow[group].balanceAmount : "") +
              '"'
          );
        }
        row.push('"' + $filter("centsToDollars")(dataRow ? dataRow.total.balanceAmount : "") + '"');

        rows.push(row);
      }
    }

    const totalRow = [""];
    for (const groupTotal of $scope.summaryGroupTotals) {
      totalRow.push(
        '"' + $filter("centsToDollars")(groupTotal ? groupTotal.balanceAmount : "") + '"'
      );
    }
    rows.push(totalRow);

    var csvContent = "";
    rows.forEach(function (rowArray) {
      var row = rowArray.join(",");
      csvContent += row + "\r\n";
    });

    var encodedUri = "data:text/csv;charset=utf-8," + encodeURIComponent(universalBOM + csvContent);
    var link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", getExportedFileName());
    document.body.appendChild(link);

    link.click();
    $scope.loadingCSV = false;
  };

  $scope.exportTableDetailed = (selected) => {
    $scope.loadingCSV = true;
    const rows = [];
    const titles = [
      "Service",
      "Visit ID",
      "Patient",
      "Patient Display ID",
      "Patient ID",
      "Admission ID",
      "Medicaid ID",
      // "Caregiver",
      "Visit Date",
      "Schedule Time",
      // "Recorded Time",
      "Contract",
      "Service Code",
      "Service Code ID",
      "Office",
      "Invoice Display ID",
      "Invoice ID",
      "Invoice Date",
      "Units",
      "Amount",
      "Paid",
      "Cost Share Attributed",
      "Cost Share Denied",
      "Cost Share Period",
      "Cost Share Frequency",
      "Balance",
      "Payment Status",
      "No Payment Reason",
      "Collection Status",
      "Claim Status",
      "Check Numbers",
      "Claim Numbers",
      "Adjustment Code",
      "Last Export Date",
      "Export Dates",
      "Note",
      "Follow Up Date",
      "Follow Up For",
      "Note Created At",
      "Note Created By",
      "Notes",
    ].filter((title) => $scope.isColumnVisible(title));
    rows.push(titles.map((title) => (title === "Visit Date" ? "Service Date" : title)));

    const items = selected
      ? $scope.selectionLogic.getSelectedItems()
      : $scope.selectionLogic.data.items;

    // Filter table data by global filter
    const allAr = $filter("filter")(items, $scope.globalFilter.val);

    allAr.forEach((dataRow) => {
      const csvRow = [];
      titles.forEach((title) => {
        if (!$scope.isColumnVisible(title)) {
          return;
        }

        let toPush = "";
        switch (title) {
          case "Service":
            toPush = dataRow.service;
            break;
          case "Visit ID":
            toPush = dataRow.visitInstanceId;
            break;
          case "Patient":
            toPush = dataRow.patientName;
            break;
          case "Patient Display ID":
            toPush = dataRow.patientDisplayId;
            break;
          case "Patient ID":
            toPush = dataRow.patientId;
            break;
          case "Admission ID":
            toPush = dataRow.admissionId;
            break;
          case "Medicaid ID":
            toPush = dataRow.medicaidId;
            break;
          // case "Caregiver":
          //     toPush = dataRow.caregiver.displayName || '';
          //     break;
          case "Visit Date":
            toPush = $filter("mfShortDate")(dataRow.visitDate);
            break;
          case "Schedule Time":
            toPush = dataRow.isCostShare
              ? ""
              : $filter("hourMinutesRange")(dataRow, "startTime", "endTime");
            break;
          // case "Recorded Time":
          //     toPush = $filter("hourMinutesRange")(dataRow, 'clockinTime', 'clockoutTime');
          //     break;
          case "Contract":
            toPush = dataRow.contractTypeName;
            break;
          case "Service Code":
            toPush = dataRow.serviceCodeCode;
            break;
          case "Service Code ID":
            toPush = dataRow.serviceCodeId;
            break;
          case "Office":
            toPush = dataRow.officeName;
            break;
          case "Invoice Display ID":
            toPush = dataRow.invoiceDisplayId;
            break;
          case "Invoice ID":
            toPush = dataRow.invoiceId;
            break;
          case "Invoice Date":
            toPush = $filter("mfShortDate")(dataRow.invoiceCreatedAt);
            break;
          case "Units":
            toPush = dataRow.isCostShare ? "" : dataRow.billingUnitsThousandths / 1000;
            break;
          case "Amount":
            toPush = $filter("centsToDollars")(dataRow.billedAmount) ?? 0;
            break;
          case "Paid":
            toPush = $filter("centsToDollars")(dataRow.payment) ?? 0;
            break;
          case "Cost Share Period":
            toPush = dataRow.costSharePeriod || "";
            break;
          case "Cost Share Frequency":
            toPush = dataRow.costShareFrequency || "";
            break;
          case "Cost Share Attributed":
            toPush = dataRow.isCostShare
              ? ""
              : $filter("centsToDollars")(dataRow.costShareAttributedAmount ?? 0);
            break;
          case "Cost Share Denied":
            toPush = dataRow.isCostShare
              ? ""
              : $filter("centsToDollars")(dataRow.costShareDeniedAmount ?? 0);
            break;
          case "Balance":
            toPush = $filter("centsToDollars")(dataRow.balance) ?? 0;
            break;
          case "Payment Status":
            toPush = dataRow.paymentStatus;
            break;
          case "No Payment Reason":
            toPush = dataRow.latestNote ? dataRow.latestNote.arReasonText : "";
            break;
          case "Collection Status":
            toPush = dataRow.latestNote ? dataRow.latestNote.collectionStatusText : "";
            break;
          case "Claim Status":
            toPush = dataRow.latestNote ? dataRow.latestNote.claimStatusText : "";
            break;
          case "Check Numbers":
            toPush = dataRow.checkNumbers?.map?.((c) => c.checkNumber).join?.(", ");
            break;
          case "Claim Numbers":
            toPush = dataRow.claimNumbers?.join?.(", ");
            break;
          case "Adjustment Code":
            toPush = dataRow.adjustment
              ? dataRow.adjustment.groupCode + ":" + dataRow.adjustment.reason
              : "";
            break;
          case "Last Export Date":
            toPush = dataRow.lastExportDate ? $filter("mfShortDate")(dataRow.lastExportDate) : "";
            break;
          case "Export Dates":
            toPush = dataRow.exportDates.map((date) => $filter("mfShortDate")(date)).join(", ");
            break;
          case "Note":
            toPush = dataRow.latestNote ? dataRow.latestNote.noteText : "";
            break;
          case "Follow Up Date":
            toPush = dataRow.latestNote
              ? $filter("mfShortDate")(dataRow.latestNote.followUpDate)
              : "";
            break;
          case "Follow Up For":
            toPush = dataRow.latestNote
              ? $scope.agencyMemberNamesMap[dataRow.latestNote.followUpFor]
              : "";
            break;
          case "Note Created At":
            toPush = dataRow.latestNote
              ? $filter("mfShortTime")(dataRow.latestNote.createdAt, ["withDate"])
              : "";
            break;
          case "Note Created By":
            toPush = dataRow.latestNote
              ? $scope.agencyMemberNamesMap[dataRow.latestNote.createdBy]
              : "";
            break;
          case "Notes":
            if (!dataRow.notes || !Array.isArray(dataRow.notes) || dataRow.notes.length === 0) {
              toPush = "-";
            } else {
              const amountText = `${dataRow.notes.length} note${
                dataRow.notes.length > 1 ? "s" : ""
              }`;
              const notesContents = dataRow.notes
                .map(
                  (note) =>
                    `${$filter("mfShortTime")(note.createdAt, ["withDate"])}: ${note.noteText}`
                )
                .join(" | ");
              toPush = `${amountText}: ${notesContents}`;
            }
            break;
          default:
            break;
        }

        const stringValue = String(toPush ?? "").replaceAll('"', '""');

        csvRow.push('"' + stringValue + '"');
      });

      rows.push(csvRow);
    });

    var csvContent = "";
    rows.forEach(function (rowArray) {
      var row = rowArray.join(",");
      csvContent += row + "\r\n";
    });

    var encodedUri = "data:text/csv;charset=utf-8," + encodeURIComponent(universalBOM + csvContent);
    var link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", getExportedFileName());
    document.body.appendChild(link);

    link.click();
    $scope.loadingCSV = false;
  };

  var getExportedFileName = function () {
    return `medflyt-export-ar-${$filter("date")(new Date(), "yyyy-MM-dd")}.csv`;
  };

  // TABLE COLUMNS LOCAL STORAGE PREPARE

  // Patient
  // Caregiver
  // Visit Date
  // Schedule time
  // Recorded time
  // Contract
  // Office
  // Invoice
  // Invoice Date
  // Units
  // Amount
  // Paid
  // Balance
  // Payment Status
  // No Payment Reason
  // Collection Status
  // Claim Status
  // Follow Up Date
  // Notes
  // Action

  $scope.getSelectedSums = function () {
    let billedAmount = 0;
    let paidAmount = 0;
    let balance = 0;

    const selection = $scope.selectionLogic.getSelectedItems();

    for (const selected of selection) {
      billedAmount += selected.billedAmount;
      paidAmount += selected.payment;
      // Not billed - paid: with cost share on, the row's balance also nets out
      // the MA64 denial.
      balance += selected.balance;
    }

    return {
      billedAmount,
      paidAmount,
      balance,
    };
  };

  $scope.goToInvoicePage = (invoiceId) => {
    $rootScope.openInvoiceModal({ invoiceId });
  };

  $scope.goToCheckPage = (id) => {
    $state.go("app.billing.check", { id: id, check: null });
  };

  $scope.goToArNotesUploader = function () {
    var controlData = {
      previousPageUrl: "app.billing.accountsReceivable",

      // temporary implementation for the 'createDataToUpload' method
      createDataToUpload: function (scope) {
        var formData = new FormData();
        formData.append("file", scope.file, scope.file.name);
        return formData;
      },

      uploadPostUrl:
        "agencies/" +
        $rootScope.agencyId +
        "/agency_members/" +
        $rootScope.agencyMemberId +
        "/upload_ar_notes",
      fileTypeName: "plain-text",
      isProcessJobUpload: true,
      caption: "Upload AR notes",
      subcaption:
        "Upload CSV or Excel notes. Follow-up member accepts an ID or full name. Track results in Uploads; the AR notes tab includes examples and valid references.",
      css: "billing-page",
    };
    $state.go("app.billing.uploadArNotes", { data: controlData });
  };

  initialize();
}
